import { useEffect, useSyncExternalStore } from 'react';
import { useRouter } from 'next/router';
import {
  getServerUnreadCount,
  getUnreadCount,
  loadUnreadCount,
  subscribeUnreadCount,
} from './unread-notifications-store';

export { decrementUnreadNotifications, refreshUnreadNotifications } from './unread-notifications-store';

/**
 * The unread-notification count behind the sidebar bell's dot — see
 * `unread-notifications-store.ts` for what the number means and how it
 * reconciles.
 *
 * Cheap by design — no polling loop, and every mounted bell shares one store:
 *  - read once on mount (the sidebar is mounted for the whole session by AppFrame);
 *  - re-read on every route CHANGE (`router.pathname`) — including onto and away from
 *    /notifications, which under the unread definition no longer races the page's
 *    clear-on-open mark (that mark moves UNSEEN→SEEN_BUT_UNREAD, and the count spans
 *    both, so it can't change the number). Shallow query-only changes (switching a
 *    conversation via `?conversation=`) don't change `pathname`, so they cost nothing;
 *  - re-read when a page reports it mutated read-state (`refreshUnreadNotifications`).
 *
 * The bells' reads are single-flighted by the store, so a route change costs ONE
 * request no matter how many bells are mounted.
 */
export function useUnreadNotifications(): number {
  const { pathname } = useRouter();
  const unreadCount = useSyncExternalStore(subscribeUnreadCount, getUnreadCount, getServerUnreadCount);

  useEffect(() => {
    void loadUnreadCount();
  }, [pathname]);

  return unreadCount;
}
